import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

function importAll(context) {
  return context.keys().map(context);
}

const portraits = importAll(
  require.context("./images/header", false, /\.(jpg|jpeg|png|webp)$/i)
);

const SWIPE_THRESHOLD = 50;

function Header() {
  const images = useMemo(() => portraits, []);

  const [visibleIndex, setVisibleIndex] = useState(0);

  const transitionLockRef = useRef(false);
  const swipeStartRef = useRef(null);
  const didSwipeRef = useRef(false);

  const preloadImage = useCallback((src) => {
    return new Promise((resolve) => {
      const image = new Image();

      const finish = () => resolve();

      image.onload = finish;
      image.onerror = finish;
      image.src = src;

      if (image.complete) {
        finish();
      }
    });
  }, []);

  const changeImage = useCallback(
    async (newIndex) => {
      if (!images.length) return;
      if (newIndex === visibleIndex) return;
      if (transitionLockRef.current) return;

      transitionLockRef.current = true;

      // Wait until the next image is ready before changing what is displayed.
      await preloadImage(images[newIndex]);

      setVisibleIndex(newIndex);

      // Briefly prevent accidental double clicks/swipes.
      window.setTimeout(() => {
        transitionLockRef.current = false;
      }, 100);
    },
    [images, preloadImage, visibleIndex]
  );

  const goNext = useCallback(() => {
    if (!images.length) return;

    changeImage((visibleIndex + 1) % images.length);
  }, [changeImage, images.length, visibleIndex]);

  const goPrev = useCallback(() => {
    if (!images.length) return;

    changeImage((visibleIndex - 1 + images.length) % images.length);
  }, [changeImage, images.length, visibleIndex]);

  useEffect(() => {
    if (!images.length) return;

    const nextIndex = (visibleIndex + 1) % images.length;
    const previousIndex =
      (visibleIndex - 1 + images.length) % images.length;

    preloadImage(images[nextIndex]);
    preloadImage(images[previousIndex]);
  }, [images, preloadImage, visibleIndex]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [goNext, goPrev]);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === "mouse") return;

    didSwipeRef.current = false;

    swipeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
  }, []);

  const handlePointerUp = useCallback(
    (event) => {
      if (event.pointerType === "mouse") return;
      if (!swipeStartRef.current) return;

      const { x: startX, y: startY } = swipeStartRef.current;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      swipeStartRef.current = null;

      const isHorizontalSwipe =
        Math.abs(deltaX) >= SWIPE_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY);

      if (!isHorizontalSwipe) return;

      didSwipeRef.current = true;

      if (deltaX < 0) {
        goNext();
      } else {
        goPrev();
      }
    },
    [goNext, goPrev]
  );

  const handlePointerCancel = useCallback(() => {
    swipeStartRef.current = null;
    didSwipeRef.current = false;
  }, []);

  const handleZoneClick = useCallback(
    (direction) => {
      // Stops the synthetic click that can follow a touch swipe.
      if (didSwipeRef.current) {
        didSwipeRef.current = false;
        return;
      }

      if (direction === "previous") {
        goPrev();
      } else {
        goNext();
      }
    },
    [goNext, goPrev]
  );

  if (!images.length) {
    return null;
  }

  const activeImage = images[visibleIndex];

  return (
    <header className="relative w-full px-4 pb-10 pt-24 sm:px-6 sm:pt-28 md:px-8 md:pb-14 md:pt-32 lg:pt-20 xl:px-10 xl:pt-16">
      <div className="mx-auto w-full max-w-[1800px]">
        <div
          className="relative flex h-[78svh] w-full touch-pan-y items-center justify-center lg:h-[calc(100svh-2rem)]"
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label={`Portfolio image ${visibleIndex + 1} of ${images.length}`}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        >
          <figure className="flex h-full w-full items-center justify-center">
            <img
              src={activeImage}
              alt={`Portfolio photograph ${visibleIndex + 1} of ${images.length}`}
              className="block h-full w-full select-none object-contain"
              loading="eager"
              decoding="async"
              draggable="false"
              fetchPriority={visibleIndex === 0 ? "high" : "auto"}
            />
          </figure>

          <button
            type="button"
            onClick={() => handleZoneClick("previous")}
            className="absolute inset-y-0 left-0 z-20 w-1/2 cursor-w-resize border-0 bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-900"
            aria-label="Previous image"
          >
            <span className="sr-only">Previous image</span>
          </button>

          <button
            type="button"
            onClick={() => handleZoneClick("next")}
            className="absolute inset-y-0 right-0 z-20 w-1/2 cursor-e-resize border-0 bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-900"
            aria-label="Next image"
          >
            <span className="sr-only">Previous image</span>
          </button>

          <span
            className="pointer-events-none absolute bottom-1 left-1/2 z-30 -translate-x-1/2 text-[10px] font-medium uppercase tracking-[0.22em] text-stone-500 sm:bottom-3"
            aria-live="polite"
          >
            {String(visibleIndex + 1).padStart(2, "0")} /{" "}
            {String(images.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </header>
  );
}

export default Header;